import React, { useEffect, useMemo, useRef, useState } from "react";
import * as amplitude from "@amplitude/analytics-browser";

import {
  generatePuzzle,
  generateHiddenSlots,
  buildPoolValues,
  GAME_LEVELS,
} from "./Puzzle.data";

import LeftPart from "./components/LeftPart";
import RightPart from "./components/RightPart";

import { gameId } from "../../../hooks/getGameId";
import { updateStorage } from "../../../hooks/useStorage";
import { updateGameStorage } from "../../../hooks/useGameStorage";
import { useUpdateReduxState } from "../../../hooks/useUpdateReduxState";

import {
  animationDurationToShowscoresPopup,
} from "../../../config";
import { useParams } from "react-router-dom";

export const Puzzle = ({
  state,
  count,
  handleOpenScore,
  handleUpdateProgress,
  point,
  subscription,
  level,
  onQuestionChange,
}) => {
  const { name, gameName } = useParams();

  const updateState = useUpdateReduxState();
  const [usedIndexes, setUsedIndexes] = useState({});
  const [puzzle, setPuzzle] = useState(() =>
    generatePuzzle(level, gameName)
  );

  const [board, setBoard] = useState(puzzle.board);
  const [lines] = useState(puzzle.lines);

  const [hiddenSlots] = useState(() =>
    generateHiddenSlots(
      puzzle.lines,
      GAME_LEVELS[level].hintPercentage
    )
  );

  const [poolValues, setPoolValues] = useState(() =>
    buildPoolValues(hiddenSlots, level)
  );


  const [selectedPoolIndex, setSelectedPoolIndex] = useState(null);
  const [placedValues, setPlacedValues] = useState({});
  const [answerState, setAnswerState] = useState({});
  const score = useRef(0);

  useEffect(() => {
    amplitude.track(`${name}_${gameName}_start`);
  }, []);


  const slotExpected = useMemo(() => {
    const map = {};

    hiddenSlots.forEach((slot) => {
      map[slot.key] = slot.value;
    });

    return map;
  }, [hiddenSlots]);

  const handleRemove = (slotKey) => {
    const oldValue = placedValues[slotKey];
  
    setPlacedValues((prev) => {
      const updated = { ...prev };
      delete updated[slotKey];
      return updated;
    });
  
    setAnswerState((prev) => {
      const updated = { ...prev };
      delete updated[slotKey];
      return updated;
    });
  
    const oldIndex = poolValues.findIndex(
      (v, i) => v === oldValue && usedIndexes[i]
    );
  
    if (oldIndex !== -1) {
      setUsedIndexes(prev => ({
        ...prev,
        [oldIndex]: false
      }));
    }
  };

  const markUsed = (index, value = true) => {
    setUsedIndexes(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const handlePlace = (slotKey, poolIndex) => {
    const value = poolValues[poolIndex];
    const expected = slotExpected[slotKey];
  
    if (value == null) return;
  
    setPlacedValues((prev) => {
      const oldValue = prev[slotKey];
      const updated = { ...prev, [slotKey]: value };
  
      // 🔥 если уже был старый ответ → освободить старый index
      if (oldValue !== undefined) {
        const oldIndex = poolValues.findIndex(
          (v, i) => v === oldValue && usedIndexes[i]
        );
  
        if (oldIndex !== -1) {
          markUsed(oldIndex, false);
        }
      }
  
      const isCorrect = value === expected;
  
      setAnswerState((prev) => ({
        ...prev,
        [slotKey]: isCorrect ? "correct" : "wrong",
      }));
  
      if (isCorrect) {
        score.current += point[level];
  
        if (!subscription) {
          updateStorage(point[level]);
        }
  
        onQuestionChange?.(`slot ${slotKey} = ${value}`);
      } else {
        if (!subscription) {
          updateStorage(-point[level]);
        }
      }
  
      return updated;
    });
  
    markUsed(poolIndex, true);
  
    setSelectedPoolIndex(null);
  };

  useEffect(() => {
    const done = Object.keys(slotExpected).every(
      (k) => placedValues[k] === slotExpected[k]
    );

    if (!done) return;

    if (subscription) {
      updateState({
        userId: localStorage.getItem("user"),
        profileId: JSON.parse(localStorage.getItem("profile"))?._id,
        game: name,
        score: score.current,
        id: gameId(name, gameName),
        collection: name,
      });
    } else {
      handleUpdateProgress();
      updateGameStorage(name, gameName, score.current, true);
    }

    setTimeout(() => {
      handleOpenScore();
      localStorage.setItem(`${name + '_' + gameName + '_' + level + '_score'}`, score.current);
    }, animationDurationToShowscoresPopup * 1000);
  }, [placedValues, slotExpected]);


  return (
    <div className="w-full h-full flex justify-between relative z-30">
      <LeftPart
        board={board}
        lines={lines}
        placedValues={placedValues}
        answerState={answerState}
        slotExpected={slotExpected}
        selectedPoolIndex={selectedPoolIndex}
        onPlace={handlePlace}
        state={state}
        onRemove={handleRemove}
      />

      <RightPart
        poolValues={poolValues}
        selectedPoolIndex={selectedPoolIndex}
        setSelectedPoolIndex={setSelectedPoolIndex}
        onPlace={handlePlace}
        state={state}
        usedIndexes={usedIndexes}
        setUsedIndexes={setUsedIndexes}
      />
    </div>
  );
};

export default Puzzle;